import React from 'react';
import { MetaradarStateSnapshot } from '@app/model/metaradar/MetaradarStateSnapshot';
import DataProvider from '@app/model/_common/DataProvider';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import Message from '@app/component/message/Message';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Utilities from '@util/Utilities';
import { findProviderData } from '@app/component/provider/FindProviderData';
import _sortBy from 'lodash/sortBy';
import { RegionColors } from '@app/component/form/color/PaletteColors';

const MetaradarGraphUndercutsPerRegionForProviderFragment: React.FunctionComponent<{
	metaradarStateSnapshot: MetaradarStateSnapshot | undefined,
	provider: DataProvider
}> = ({ metaradarStateSnapshot, provider }) => {

	if (!metaradarStateSnapshot) {
		return <div className="p-6"><Message state="info">No results found!</Message></div>;
	}

	const { companyHotel } = useSelectedCompanyHotel();

	const undercutsPerRegionForProvider = metaradarStateSnapshot.findUndercutsPerRegionForProvider(companyHotel.metaradarSettings?.threshold || 0.9, provider);

	if (!Array.from(undercutsPerRegionForProvider.values()).some(entry => entry.count > 0)) {
		return <div className="p-6"><Message state="info">No results found!</Message></div>;
	}

	const chartData = Array.from(undercutsPerRegionForProvider.entries())
		.filter(([, value]) => value.count > 0)
		.map(([key, value]) => ({
			name: Utilities.snakeCaseToWords(value.region),
			region: value.region,
			y: parseInt(Utilities.formatDouble((value.count/value.all)*100, 0)),
			avgUndercutPercentage: parseInt(Utilities.formatDouble(value.avgUndercutPercentage, 0)),
			count: value.count,
			all: value.all
		}));

	const sortedChartData = _sortBy(chartData, d => d.name);

	const colors = sortedChartData.map(ch => RegionColors.get(ch.region)!);

	const chartOptions: Highcharts.Options = {
		chart: {
			type: 'column',
			backgroundColor: '#FFFFFF',
		},
		title: {
			text: findProviderData(provider).name,
			style: { color: '#000' },
		},
		xAxis: {
			categories: sortedChartData.map(d => d.name),
			title: {
				style: { color: '#000' },
			},
			labels: {
				style: { color: '#000' },
			},
			lineColor: '#E5E7EB',
		},
		yAxis: {
			min: 0,
			title: {
				text: '% of Undercuts',
				style: { color: '#000' },
			},
			labels: {
				style: { color: '#000' },
			},
			gridLineColor: '#E5E7EB',
			lineColor: '#E5E7EB',
			tickPixelInterval: 40,
		},
		tooltip: {
			formatter: function () {
				// @ts-ignore
				const point = this.point;
				return `<div>
								<b class="text-sm text-center">${point.name}</b><br/><br/>
								<b class="text-xs">Percentage of Undercuts:</b> ${point.y}% (${point.count}/${point.all})<br/>
								<b class="text-xs">Average Undercut:</b> ${point.avgUndercutPercentage}%
						</div>`;
			},
		},
		series: [{
			type: 'column',
			name: 'Undercuts',
			data: sortedChartData,
			colorByPoint: true,
			colors: colors,
		}],
		legend: {
			enabled: false,
		},
		credits: {
			enabled: false,
		},
	};

	return (
		<div className="p-4">
			<HighchartsReact highcharts={Highcharts} options={chartOptions} />
		</div>
	);

};

export default MetaradarGraphUndercutsPerRegionForProviderFragment;